import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Save, Copy, AlertTriangle, FileText, Package, Settings2 } from 'lucide-react'
import type { VqlArtefact } from '../../types/velociraptor'
import { ArtefactBrowser } from './ArtefactBrowser'
import { CollectorSettingsForm, type CollectorSettings } from './CollectorSettingsForm'
import type { TemplateSaveAction } from '../../utils/template-permissions'
import type { ActiveSection, ParameterOverrides } from './velociraptor-config-modal.state'

interface ConfigModalHeaderProps {
  isEditMode: boolean
  onClose: () => void
}

export function ConfigModalHeader({ isEditMode, onClose }: ConfigModalHeaderProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
        {isEditMode
          ? t('velociraptor.configModal.editTitle', 'Edit Collector Template')
          : t('velociraptor.configModal.createTitle', 'Create Collector Template')}
      </h3>
      <button
        onClick={onClose}
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={t('velociraptor.configModal.close', 'Close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface SectionTabProps {
  active: boolean
  onClick: () => void
  icon: ReactNode
  label: string
  badge?: number | undefined
}

function SectionTab({ active, onClick, icon, label, badge }: SectionTabProps): ReactElement {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
        active
          ? 'border-[#4e5e82] text-[#4e5e82] dark:border-[#8a9bba] dark:text-[#8a9bba]'
          : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-300'
      }`}
    >
      {icon}
      {label}
      {badge !== undefined && (
        <span className="dark:bg-[#8a9bba]/15/50 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#4e5e82]/10 px-1.5 text-xs font-medium text-[#5c6e94] dark:text-[#8a9bba]">
          {badge}
        </span>
      )}
    </button>
  )
}

interface ConfigTabsBarProps {
  activeSection: ActiveSection
  selectedCount: number
  onSelect: (section: ActiveSection) => void
}

export function ConfigTabsBar({
  activeSection,
  selectedCount,
  onSelect,
}: ConfigTabsBarProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-shrink-0 border-b border-slate-200 px-6 dark:border-slate-700">
      <SectionTab
        active={activeSection === 'metadata'}
        onClick={() => {
          onSelect('metadata')
        }}
        icon={<FileText className="h-4 w-4" />}
        label={t('velociraptor.configModal.metadataTab', 'Details')}
      />
      <SectionTab
        active={activeSection === 'artefacts'}
        onClick={() => {
          onSelect('artefacts')
        }}
        icon={<Package className="h-4 w-4" />}
        label={t('velociraptor.configModal.artefactsTab', 'Artefacts')}
        badge={selectedCount > 0 ? selectedCount : undefined}
      />
      <SectionTab
        active={activeSection === 'settings'}
        onClick={() => {
          onSelect('settings')
        }}
        icon={<Settings2 className="h-4 w-4" />}
        label={t('velociraptor.configModal.settingsTab', 'Settings')}
      />
    </div>
  )
}

interface ArtefactsSectionProps {
  artefacts: VqlArtefact[]
  artefactsByCategory: Record<string, VqlArtefact[]>
  selectedArtefacts: string[]
  parameterOverrides: ParameterOverrides
  loading: boolean
  error: string | null
  onSelect: (name: string) => void
  onDeselect: (name: string) => void
  onParameterChange: (artefactName: string, paramName: string, value: string) => void
  onRefresh: () => void
}

function ArtefactsErrorPanel({ error }: { error: string }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/50"
      role="alert"
    >
      <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
      <div>
        <p className="text-sm font-medium text-red-700 dark:text-red-400">
          {t('velociraptor.configModal.connectionError', 'Cannot connect to Velociraptor server')}
        </p>
        <p className="mt-1 text-xs text-red-600 dark:text-red-500">{error}</p>
      </div>
    </div>
  )
}

function ArtefactsSection({
  artefacts,
  artefactsByCategory,
  selectedArtefacts,
  parameterOverrides,
  loading,
  error,
  onSelect,
  onDeselect,
  onParameterChange,
  onRefresh,
}: ArtefactsSectionProps): ReactElement {
  return (
    <div className="h-[50vh]">
      {error ? (
        <ArtefactsErrorPanel error={error} />
      ) : (
        <ArtefactBrowser
          artefacts={artefacts}
          artefactsByCategory={artefactsByCategory}
          selectedArtefacts={selectedArtefacts}
          parameterOverrides={parameterOverrides}
          loading={loading}
          onSelect={onSelect}
          onDeselect={onDeselect}
          onParameterChange={onParameterChange}
          onRefresh={onRefresh}
        />
      )}
    </div>
  )
}

interface ConfigBodySwitchProps {
  activeSection: ActiveSection
  metadata: MetadataSectionProps
  artefacts: ArtefactsSectionProps
  settings: { value: CollectorSettings; onChange: (value: CollectorSettings) => void }
}

export function ConfigBodySwitch({
  activeSection,
  metadata,
  artefacts,
  settings,
}: ConfigBodySwitchProps): ReactElement {
  return (
    <div className="flex-1 overflow-y-auto px-6 py-4">
      {activeSection === 'metadata' && <MetadataSection {...metadata} />}
      {activeSection === 'artefacts' && <ArtefactsSection {...artefacts} />}
      {activeSection === 'settings' && (
        <CollectorSettingsForm settings={settings.value} onChange={settings.onChange} />
      )}
    </div>
  )
}

interface SaveButtonProps {
  saving: boolean
  saveAction: TemplateSaveAction
  isEditMode: boolean
  canSave: boolean
  onSave: () => void
}

function SaveButton({
  saving,
  saveAction,
  isEditMode,
  canSave,
  onSave,
}: SaveButtonProps): ReactElement {
  const { t } = useTranslation('cases')
  const useCopyIcon = saveAction === 'save_as' && isEditMode
  const label = useCopyIcon
    ? t('velociraptor.configModal.saveAsNew', 'Save as New Template')
    : t('velociraptor.configModal.save', 'Save Template')
  return (
    <button
      onClick={onSave}
      disabled={!canSave || saving}
      className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] focus:ring-2 focus:ring-[#4e5e82] focus:outline-none disabled:opacity-50"
    >
      {saving ? (
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
      ) : useCopyIcon ? (
        <Copy className="h-4 w-4" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      {label}
    </button>
  )
}

interface ConfigSaveBarProps {
  errorMessage: string | null
  selectedCount: number
  saving: boolean
  saveAction: TemplateSaveAction
  isEditMode: boolean
  canSave: boolean
  onSave: () => void
  onClose: () => void
}

export function ConfigSaveBar({
  errorMessage,
  selectedCount,
  saving,
  saveAction,
  isEditMode,
  canSave,
  onSave,
  onClose,
}: ConfigSaveBarProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex-shrink-0 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      {errorMessage && (
        <div
          className="mb-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
          role="alert"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {errorMessage}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {selectedCount === 0
            ? t('velociraptor.configModal.noArtefactsSelected', 'No artefacts selected')
            : t('velociraptor.configModal.artefactsSummary', '{{count}} artefact(s) selected', {
                count: selectedCount,
              })}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <SaveButton
            saving={saving}
            saveAction={saveAction}
            isEditMode={isEditMode}
            canSave={canSave}
            onSave={onSave}
          />
        </div>
      </div>
    </div>
  )
}

interface MetadataSectionProps {
  name: string
  description: string
  platforms: string[]
  onNameChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onTogglePlatform: (platform: string) => void
}

const PLATFORM_OPTIONS = [
  { value: 'windows', label: 'Windows' },
  { value: 'linux', label: 'Linux' },
  { value: 'macos', label: 'macOS' },
] as const

interface PlatformPickerProps {
  platforms: string[]
  onTogglePlatform: (platform: string) => void
}

function PlatformPicker({ platforms, onTogglePlatform }: PlatformPickerProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.configModal.supportedPlatforms', 'Supported Platforms')}
      </label>
      <div className="flex gap-3">
        {PLATFORM_OPTIONS.map((opt) => {
          const checked = platforms.includes(opt.value)
          return (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                checked
                  ? 'dark:bg-[#8a9bba]/20/30 border-[#8a9bba] bg-[#4e5e82]/5 dark:border-[#5c6e94]'
                  : 'border-slate-200 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-800'
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => { onTogglePlatform(opt.value) }}
                className="rounded border-slate-300 text-[#4e5e82] focus:ring-[#4e5e82]"
              />
              <span className="text-sm text-slate-700 dark:text-slate-300">{opt.label}</span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

function MetadataSection({
  name,
  description,
  platforms,
  onNameChange,
  onDescriptionChange,
  onTogglePlatform,
}: MetadataSectionProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="max-w-xl space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.configModal.templateName', 'Template Name')}
          <span className="ml-0.5 text-[#4e5e82]">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => { onNameChange(e.target.value) }}
          maxLength={100}
          placeholder={t('velociraptor.configModal.namePlaceholder', 'e.g. Windows Triage - Full')}
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('velociraptor.configModal.templateDescription', 'Description')}
        </label>
        <textarea
          value={description}
          onChange={(e) => { onDescriptionChange(e.target.value) }}
          rows={3}
          placeholder={t(
            'velociraptor.configModal.descriptionPlaceholder',
            'Describe the purpose of this template...'
          )}
          className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
        />
      </div>
      <PlatformPicker platforms={platforms} onTogglePlatform={onTogglePlatform} />
    </div>
  )
}
